import { useEffect, useRef } from "react";
import { sampleEkg } from "../lib/ekg";

export function drawGrid(ctx, x0, y0, w, h, cell = 20) {
  ctx.strokeStyle = "hsl(142 50% 20% / 0.35)";
  ctx.lineWidth = 1;
  for (let x = x0; x <= x0 + w; x += cell) {
    ctx.beginPath(); ctx.moveTo(x, y0); ctx.lineTo(x, y0 + h); ctx.stroke();
  }
  for (let y = y0; y <= y0 + h; y += cell) {
    ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(x0 + w, y); ctx.stroke();
  }
}

export function drawTrace(ctx, points, x0, y0, w, h, ampRatio = 0.3, glow = 10) {
  ctx.strokeStyle = "hsl(142 71% 55%)";
  ctx.lineWidth = glow > 4 ? 2 : 1.5;
  ctx.shadowColor = "hsl(142 71% 55%)";
  ctx.shadowBlur = glow;
  ctx.beginPath();
  const amp = h * ampRatio;
  const mid = y0 + h / 2;
  const dx = w / Math.max(1, points.length - 1);
  for (let i = 0; i < points.length; i++) {
    const px = x0 + i * dx;
    const py = mid - points[i] * amp;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.stroke();
  ctx.shadowBlur = 0;
}

export function setupCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener("resize", resize);
  return { ctx, cleanup: () => window.removeEventListener("resize", resize) };
}

export const SAMPLE_STEP = 0.004;

export function MonitorStrip({ params, onBeat, className = "h-72 sm:h-80" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !params) return;
    const { ctx, cleanup } = setupCanvas(canvas);
    const points = [];
    let t = 0, lastBeat = 0, lastY = 0, prevY = 0;
    let lastReal = performance.now();
    let raf = 0;

    const draw = (now) => {
      const { width: w, height: h } = canvas.getBoundingClientRect();
      const dt = Math.min(0.1, (now - lastReal) / 1000);
      lastReal = now;
      t += dt;
      const toAdd = Math.max(1, Math.floor(dt / SAMPLE_STEP));
      for (let i = 0; i < toAdd; i++) {
        const tt = t - (toAdd - i) * SAMPLE_STEP;
        const y = sampleEkg(tt, params);
        points.push(y);
        if (y < lastY && lastY > prevY && lastY > 0.55 && tt - lastBeat > 0.2) {
          lastBeat = tt;
          onBeat?.();
        }
        prevY = lastY;
        lastY = y;
      }
      const maxPts = Math.ceil(w / 1.2);
      if (points.length > maxPts) points.splice(0, points.length - maxPts);

      ctx.fillStyle = "hsl(240 10% 4%)";
      ctx.fillRect(0, 0, w, h);
      drawGrid(ctx, 0, 0, w, h);
      drawTrace(ctx, points, 0, 0, w, h);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); cleanup(); };
  }, [params, onBeat]);

  return <canvas ref={canvasRef} data-testid="monitor-strip" className={`w-full block ${className}`} />;
}
